import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getDateLocale } from '@/lib/utils/date'
import { PageHeader } from '@/components/layout/PageHeader'
import { CustomerList } from '@/components/customers/CustomerList'
import { BookingStatusBadge } from '@/components/bookings/BookingStatusBadge'
import { BookingDetailPanel } from '@/components/bookings/BookingDetailPanel'
import { Users, Phone, Mail, UtensilsCrossed, CalendarDays, DollarSign, Download, Smartphone } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import type { GuestProfile } from '@/lib/types/booking'
import { usePwaInstalls, type PwaInstall } from '@/lib/queries/usePwaInstalls'

export default function CustomersPage() {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<GuestProfile | null>(null)
  const [openBookingId, setOpenBookingId] = useState<string | null>(null)
  const { data: pwaResp, isLoading: pwaLoading } = usePwaInstalls()
  const pwaReport = pwaResp?.data

  return (
    <div className="relative">
      <PageHeader title={t('customers.title')} description={t('customers.description')} />
      <div className="flex min-w-0 flex-col gap-4 md:h-[calc(100dvh-12rem)] md:flex-row md:gap-6">

        {/* Left: guest list */}
        <div className="flex h-[min(42dvh,24rem)] min-h-64 w-full shrink-0 flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] md:h-full md:w-80">
          <CustomerList
            onSelectGuest={setSelected}
            selectedGuestKey={selected?.key ?? null}
          />
        </div>

        {/* Right: guest profile */}
        <div className="min-w-0 flex-1 md:overflow-y-auto">
          <div className="space-y-4">
            <PwaInstallSummary
              loading={pwaLoading}
              summary={pwaReport?.summary}
              installs={pwaReport?.installs ?? []}
            />

            {selected === null ? (
              <div className="flex flex-col items-center justify-center min-h-[16rem] text-[var(--color-text-muted)]">
                <Users className="h-10 w-10 mb-3" />
                <p className="text-sm">{t('customers.selectGuest')}</p>
              </div>
            ) : (
              <>

              {/* Profile header */}
              <div className="rounded-[var(--radius-lg)] bg-[var(--color-bg-surface)] border border-[var(--color-border-subtle)] p-5">
                <div className="flex items-start gap-4">
                  <div className="h-14 w-14 rounded-full bg-[var(--color-bg-elevated)] flex items-center justify-center text-xl font-bold text-[var(--color-text-primary)] shrink-0">
                    {(selected.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-[var(--color-text-primary)] truncate">
                      {selected.name || 'Unknown Guest'}
                    </h2>
                    <div className="flex flex-col gap-1 mt-1">
                      {selected.phone && (
                        <span className="flex items-center gap-1.5 text-sm text-[var(--color-text-muted)]">
                          <Phone className="h-3.5 w-3.5" /> {selected.phone}
                        </span>
                      )}
                      {selected.email && (
                        <span className="flex items-center gap-1.5 text-sm text-[var(--color-text-muted)]">
                          <Mail className="h-3.5 w-3.5" /> {selected.email}
                        </span>
                      )}
                      {!selected.phone && !selected.email && (
                        <span className="text-sm text-[var(--color-text-muted)]">{t('customers.noContactInfo')}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mt-4">
                  <div className="rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-3 text-center">
                    <p className="text-2xl font-bold text-[var(--color-text-primary)]">{selected.visitCount}</p>
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                      {t('customers.visits')}
                    </p>
                  </div>
                  <div className="rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-3 text-center">
                    <p className="text-2xl font-bold text-[var(--color-text-primary)]">{selected.totalPeople}</p>
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{t('customers.totalGuests')}</p>
                  </div>
                  <div className="rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] p-3 text-center">
                    <p className="text-2xl font-bold text-[var(--color-text-primary)]">
                      {Math.round(selected.totalPeople / selected.visitCount)}
                    </p>
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{t('customers.avgParty')}</p>
                  </div>
                </div>
              </div>

              {/* Booking history */}
              <div className="rounded-[var(--radius-lg)] bg-[var(--color-bg-surface)] border border-[var(--color-border-subtle)] p-4">
                <p className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wide mb-3 flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5" /> {t('customers.bookingHistory')}
                </p>
                <div className="space-y-2">
                  {selected.bookings.map((b) => (
                    <button
                      key={b.documentId}
                      onClick={() => setOpenBookingId(b.documentId)}
                      className="w-full flex items-center justify-between gap-3 p-3 rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] hover:bg-[var(--color-bg-overlay)] transition-colors text-left cursor-pointer group"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-medium text-[var(--color-text-primary)]">
                            {format(parseISO(b.date), 'EEE, MMM d yyyy', { locale: getDateLocale() })}
                          </span>
                          <BookingStatusBadge status={b.status} />
                        </div>
                        <div className="flex items-center gap-3 text-xs text-[var(--color-text-muted)]">
                          <span>{b.time}</span>
                          <span className="flex items-center gap-1">
                            <Users className="h-3 w-3" /> {b.people}
                          </span>
                          {b.bookingTables && b.bookingTables.length > 0 && (
                            <span className="flex items-center gap-1">
                              <UtensilsCrossed className="h-3 w-3" />
                              {b.bookingTables.map((bt) => bt.name).join(', ')}
                            </span>
                          )}
                          {b.status === 'left' && (
                            <span className="flex items-center gap-1 text-[var(--color-accent-green)]">
                              <DollarSign className="h-3 w-3" />
                              {Number(b.metadata?.spend ?? 0).toFixed(2)}
                              {Number(b.metadata?.tip ?? 0) > 0 && (
                                <span className="text-[var(--color-text-muted)]">
                                  {t('customers.tip', { amount: Number(b.metadata?.tip ?? 0).toFixed(2) })}
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              </>
            )}
          </div>
        </div>
      </div>

      <BookingDetailPanel
        documentId={openBookingId}
        onClose={() => setOpenBookingId(null)}
      />
    </div>
  )
}

function PwaInstallSummary({
  loading,
  summary,
  installs,
}: {
  loading: boolean
  summary?: { total: number; linkedCustomers: number; anonymous: number }
  installs: PwaInstall[]
}) {
  const recent = installs.slice(0, 5)

  return (
    <div className="rounded-[var(--radius-lg)] bg-[var(--color-bg-surface)] border border-[var(--color-border-subtle)] p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wide flex items-center gap-1.5">
            <Download className="h-3.5 w-3.5" /> Customer page app installs
          </p>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            Tracks installed restaurant PWAs. Customer details appear after the same installed app places an order or reservation.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <InstallMetric label="Total" value={loading ? '-' : summary?.total ?? 0} />
          <InstallMetric label="Known" value={loading ? '-' : summary?.linkedCustomers ?? 0} />
          <InstallMetric label="Anonymous" value={loading ? '-' : summary?.anonymous ?? 0} />
        </div>
      </div>

      <div className="mt-4 divide-y divide-[var(--color-border-subtle)] overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border-subtle)]">
        {loading ? (
          <div className="p-3 text-sm text-[var(--color-text-muted)]">Loading installs...</div>
        ) : recent.length === 0 ? (
          <div className="p-3 text-sm text-[var(--color-text-muted)]">No PWA installs recorded yet.</div>
        ) : (
          recent.map((install) => <InstallRow key={install.documentId} install={install} />)
        )}
      </div>
    </div>
  )
}

function InstallMetric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="min-w-[4.5rem] rounded-[var(--radius-md)] bg-[var(--color-bg-elevated)] px-3 py-2">
      <p className="text-lg font-bold text-[var(--color-text-primary)]">{value}</p>
      <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-muted)]">{label}</p>
    </div>
  )
}

function InstallRow({ install }: { install: PwaInstall }) {
  const title = install.customerName || install.customerPhone || install.customerEmail || 'Anonymous install'
  const detail = [install.customerPhone, install.customerEmail].filter(Boolean).join(' - ')
  const lastSeen = install.lastSeenAt ? format(parseISO(install.lastSeenAt), 'MMM d, HH:mm') : 'Unknown'

  return (
    <div className="flex items-center gap-3 bg-[var(--color-bg-elevated)] p-3">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-[var(--radius-md)] bg-[var(--color-bg-surface)] text-[var(--color-text-muted)]">
        <Smartphone className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-[var(--color-text-primary)]">{title}</p>
        <p className="truncate text-xs text-[var(--color-text-muted)]">
          {detail || `${install.platform || 'Unknown platform'} - ${install.displayMode || 'installed app'}`}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-xs font-medium text-[var(--color-text-secondary)]">{lastSeen}</p>
        <p className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
          {install.launchCount ?? 0} opens
        </p>
      </div>
    </div>
  )
}
